'use client';

import { useState, useEffect } from 'react';
import { Bell, Check, X, Trash2 } from 'lucide-react';
import { NotificationService, Notification } from '@/lib/notifications';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { formatDistanceToNow } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export const NotificationBell = () => {
  const { user } = useAuth();
  const router = useRouter();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Load notifications when user is available
  useEffect(() => {
    if (user) {
      loadNotifications();
      loadUnreadCount();
    }
  }, [user]);

  const loadNotifications = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const notifs = await NotificationService.getUserNotifications(user.uid);
      setNotifications(notifs);
    } catch (error) {
      // Error loading notifications
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    if (!user) return;
    try {
      const count = await NotificationService.getUnreadCount(user.uid);
      setUnreadCount(count);
    } catch (error) {
      // Error loading unread count
    }
  };

  const handleNotificationHover = async (notification: Notification) => {
    if (!notification.read && notification.id) {
      try {
        await NotificationService.markAsRead(notification.id);
        // Update local state
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, read: true, seen: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        // Error marking as read
      }
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (notification.id) {
      try {
        await NotificationService.markAsRead(notification.id);
        // Update local state
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, read: true, seen: true } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));

        // Navigate to the link
        router.push(notification.link);
      } catch (error) {
        // Error marking as read
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;
    try {
      await NotificationService.markAllAsRead(user.uid);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true, seen: true })));
      setUnreadCount(0);
    } catch (error) {
      // Error marking all as read
    }
  };

  const handleClearAll = async () => {
    if (!user) return;

    try {
      // Delete all notifications
      await Promise.all(
        notifications.map((n) => n.id && NotificationService.deleteNotification(n.id))
      );
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      // Error clearing notifications
    }
  };

  const handleClearRead = async () => {
    if (!user) return;

    try {
      // Delete only read notifications
      const readNotifications = notifications.filter((n) => n.read && n.id);
      await Promise.all(
        readNotifications.map((n) => NotificationService.deleteNotification(n.id!))
      );
      // Keep only unread notifications
      setNotifications((prev) => prev.filter((n) => !n.read));
    } catch (error) {
      // Error clearing read notifications
    }
  };

  const handleDeleteNotification = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await NotificationService.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      // If it was unread, decrease count
      const notification = notifications.find((n) => n.id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      // Error deleting notification
    }
  };

  const formatTimestamp = (timestamp: any) => {
    try {
      const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return 'Recently';
    }
  };

  if (!user) return null;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button aria-label="Notifications" className="relative flex items-center justify-center h-10 w-10 rounded-full text-gray-300 hover:text-white hover:bg-white/10 transition-colors p-0">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <div className="absolute -top-1 -right-1 h-5 w-5 bg-red-600 rounded-full flex items-center justify-center border-2 border-black">
              <span className="text-[10px] font-bold text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            </div>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[380px] max-w-[95vw] bg-zinc-950/95 backdrop-blur-xl ring-1 ring-white/10 shadow-2xl rounded-2xl p-0 overflow-hidden"
        sideOffset={12}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-white/10">
          <h3 className="text-white font-semibold text-base">Notifications</h3>
          <div className="flex items-center gap-3">
            {notifications.filter((n) => n.read).length > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); handleClearRead(); }}
                className="text-xs text-gray-400 hover:text-white transition-colors"
              >
                Clear read
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); handleClearAll(); }}
                className="text-xs text-gray-400 hover:text-red-400 transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <div className="max-h-[480px] overflow-y-auto p-1.5">
          {loading ? (
            <div className="p-12 text-center text-gray-500">
              <div className="animate-spin rounded-full h-7 w-7 border-2 border-white/20 border-t-red-500 mx-auto mb-3"></div>
              <p className="text-sm">Loading…</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Bell className="h-10 w-10 mx-auto mb-3 text-gray-700" />
              <p className="text-sm text-gray-500">No notifications yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications.map((notification) => (
                <DropdownMenuItem
                  key={notification.id}
                  onMouseEnter={() => handleNotificationHover(notification)}
                  onClick={() => handleNotificationClick(notification)}
                  className={`relative px-3 py-3 text-gray-300 hover:text-white cursor-pointer transition-colors rounded-xl group ${
                    !notification.read ? 'bg-white/[0.07] hover:bg-white/10' : 'hover:bg-white/5'
                  }`}
                >
                  {/* Unread indicator */}
                  {!notification.read && (
                    <div className="absolute left-1 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                  )}

                  {/* Delete Button - Top Right */}
                  <button
                    onClick={(e) => notification.id && handleDeleteNotification(notification.id, e)}
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 p-1 rounded-md hover:bg-white/10"
                  >
                    <X className="h-3.5 w-3.5 text-gray-500 hover:text-white" />
                  </button>

                  <div className="flex gap-3 pl-2">

                    {/* Poster Image */}
                    {notification.posterPath && (
                      <div className="flex-shrink-0 w-12 h-[72px] rounded-lg overflow-hidden ring-1 ring-white/10">
                        <Image
                          src={`https://image.tmdb.org/t/p/w185${notification.posterPath}`}
                          alt={notification.mediaName}
                          width={64}
                          height={96}
                          className="object-cover w-full h-full"
                        />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0 pr-6">
                      <div className="space-y-1">
                        <p className="text-white font-bold text-sm leading-tight">
                          {notification.title}
                        </p>
                        <p className="text-gray-300 text-sm leading-relaxed">
                          {notification.message}
                        </p>
                        <p className="text-gray-500 text-xs mt-2 flex items-center gap-1">
                          <span className="w-1 h-1 rounded-full bg-gray-500"></span>
                          {formatTimestamp(notification.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
